import React, { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { saveOrganizationContext } from "../../store/organizationContextSlice";
import {
    getMinimumLettersMessage,
    hasMinimumLetters,
} from "../../utils/textValidation";

const initialFormState = {
    company_description: "",
    brand_voice: "",
    target_icp: "",
    core_value_proposition: "",
    website_url: "",
};

const fieldLabels = {
    company_description: "Company Description",
    brand_voice: "Brand Voice",
    target_icp: "Target ICP",
    core_value_proposition: "Core Value Proposition",
    website_url: "Website URL",
};

const textAreaFields = [
    {
        name: "company_description",
        label: fieldLabels.company_description,
        rows: 4,
        wrapperClassName: "lg:col-span-2",
        placeholder: "Describe your company, products, market, and focus.",
    },
    {
        name: "brand_voice",
        label: fieldLabels.brand_voice,
        rows: 4,
        wrapperClassName: "",
        placeholder: "Example: clear, consultative, concise, confident.",
    },
    {
        name: "target_icp",
        label: fieldLabels.target_icp,
        rows: 4,
        wrapperClassName: "",
        placeholder: "Describe the buyers, industries, regions, and roles.",
    },
    {
        name: "core_value_proposition",
        label: fieldLabels.core_value_proposition,
        rows: 4,
        wrapperClassName: "lg:col-span-2",
        placeholder: "Summarize the strongest value proposition for outreach.",
    },
];

const getInitialFormData = (data) => ({
    company_description: data?.company_description || "",
    brand_voice: data?.brand_voice || "",
    target_icp: data?.target_icp || "",
    core_value_proposition: data?.core_value_proposition || "",
    website_url: data?.website_url || "",
});

const hasExistingContext = (data) =>
    Object.values(getInitialFormData(data)).some((value) =>
        String(value).trim(),
    );

const getFieldClassName = (hasError) =>
    `w-full rounded-2xl border bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100 ${
        hasError ? "border-red-300" : "border-slate-200"
    }`;

const OrganizationContextForm = ({
    initialData,
    onSaved,
    autoFocusFirstField = false,
}) => {
    const dispatch = useDispatch();
    const { status } = useSelector((state) => state.organizationContext);
    const firstFieldRef = useRef(null);

    const [formData, setFormData] = useState(() =>
        getInitialFormData(initialData),
    );
    const [fieldErrors, setFieldErrors] = useState({});
    const [isEditing, setIsEditing] = useState(
        () => !hasExistingContext(initialData) || autoFocusFirstField,
    );
    const [shouldFocusFirstField, setShouldFocusFirstField] = useState(
        autoFocusFirstField,
    );

    useEffect(() => {
        setFormData(getInitialFormData(initialData));
        setIsEditing(!hasExistingContext(initialData) || autoFocusFirstField);
    }, [autoFocusFirstField, initialData]);

    useEffect(() => {
        if (!isEditing || !shouldFocusFirstField) return undefined;

        const frameId = window.requestAnimationFrame(() => {
            const field = firstFieldRef.current;

            if (!field) return;

            field.focus();
            const cursorPosition = field.value.length;
            field.setSelectionRange(cursorPosition, cursorPosition);
            setShouldFocusFirstField(false);
        });

        return () => window.cancelAnimationFrame(frameId);
    }, [isEditing, shouldFocusFirstField]);

    useEffect(() => {
        if (autoFocusFirstField) setShouldFocusFirstField(true);
    }, [autoFocusFirstField]);

    const validate = () => {
        const errors = {};

        Object.keys(initialFormState).forEach((fieldName) => {
            if (!formData[fieldName].trim()) {
                errors[fieldName] = `${fieldLabels[fieldName]} is required.`;
            }
        });

        textAreaFields.forEach((field) => {
            if (
                formData[field.name].trim() &&
                !hasMinimumLetters(formData[field.name])
            ) {
                errors[field.name] = getMinimumLettersMessage(field.label);
            }
        });

        return errors;
    };

    const handleChange = (fieldName, value) => {
        setFormData((currentFormData) => ({
            ...currentFormData,
            [fieldName]: value,
        }));

        if (fieldErrors[fieldName]) {
            setFieldErrors((currentErrors) => ({
                ...currentErrors,
                [fieldName]: undefined,
            }));
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const errors = validate();

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }

        try {
            const savedContext = await dispatch(
                saveOrganizationContext(formData),
            ).unwrap();

            setFieldErrors({});
            toast.success("Organization context saved successfully.");
            setIsEditing(false);
            onSaved?.(savedContext || formData);
        } catch {
            toast.error("Failed to save organization context. Please try again.");
        }
    };

    const handleStartEditing = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsEditing(true);
        setShouldFocusFirstField(true);
    };

    const handleFormSubmit = (event) => {
        if (!isEditing) {
            event.preventDefault();
            return;
        }

        return handleSubmit(event);
    };

    const isSaving = status === "loading";
    const hasRequiredValues = Object.keys(initialFormState).every((fieldName) =>
        formData[fieldName].trim(),
    );
    const hasValidTextValues = textAreaFields.every((field) =>
        hasMinimumLetters(formData[field.name]),
    );
    const isSaveDisabled = isSaving || !hasRequiredValues || !hasValidTextValues;

    return (
        <form
            onSubmit={handleFormSubmit}
            noValidate
            className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6 lg:p-8"
        >
            <div className="grid gap-5 lg:grid-cols-2">
                {textAreaFields.map((field) => (
                    <div key={field.name} className={field.wrapperClassName}>
                        <label
                            htmlFor={field.name}
                            className="mb-2 block text-sm font-semibold text-slate-800"
                        >
                            {field.label}
                        </label>
                        <textarea
                            ref={
                                field.name === "company_description"
                                    ? firstFieldRef
                                    : null
                            }
                            id={field.name}
                            rows={field.rows}
                            value={formData[field.name]}
                            disabled={!isEditing || isSaving}
                            onChange={(event) =>
                                handleChange(field.name, event.target.value)
                            }
                            placeholder={field.placeholder}
                            className={getFieldClassName(
                                fieldErrors[field.name],
                            )}
                        />
                        {fieldErrors[field.name] && (
                            <p className="mt-1.5 text-xs font-medium text-red-600">
                                {fieldErrors[field.name]}
                            </p>
                        )}
                    </div>
                ))}

                <div className="lg:col-span-2">
                    <label
                        htmlFor="website_url"
                        className="mb-2 block text-sm font-semibold text-slate-800"
                    >
                        Website URL
                    </label>
                    <input
                        id="website_url"
                        type="url"
                        value={formData.website_url}
                        disabled={!isEditing || isSaving}
                        onChange={(event) =>
                            handleChange("website_url", event.target.value)
                        }
                        placeholder="https://example.com"
                        className={getFieldClassName(
                            fieldErrors.website_url,
                        )}
                    />
                    {fieldErrors.website_url && (
                        <p className="mt-1.5 text-xs font-medium text-red-600">
                            {fieldErrors.website_url}
                        </p>
                    )}
                </div>
            </div>

            <div className="mt-7 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                {isEditing ? (
                    <button
                        type="submit"
                        disabled={isSaveDisabled}
                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
                    >
                        {isSaving && (
                            <Loader2
                                size={16}
                                className="animate-spin"
                                aria-hidden="true"
                            />
                        )}
                        {isSaving ? "Saving..." : "Save Organization Context"}
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={handleStartEditing}
                        className="inline-flex items-center justify-center rounded-xl bg-indigo-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
                    >
                        Edit Organization Context
                    </button>
                )}
            </div>
        </form>
    );
};

export default OrganizationContextForm;
